import React, { FunctionComponent, useEffect, useRef } from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { StyledSummary } from './styledComponents';
import SummaryItemComponent from './components/SummaryItem/SummaryItem.component';
import { Redirect, Route, Switch, useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import ResultsContainer from '../Results/Results.container';
import TimesContainer from '../Times/Times.container';
import CollapseButtonLineComponent from '../../components/CollapseButtonLine/CollapseButtonLine.component';
import { useQueryParams } from 'use-query-params';
import {
  AppQueryParamsConfigMap,
  TABLET_BREAKPOINT,
} from '../../app/constants';
import useWindowSize from '../../hooks/useWindowSize';
import { useEventFeed } from '../../api/useEventFeed';
import EmptyPage from '../../components/EmptyPage/EmptyPage.component';
import SplashScreenComponent from '../../components/SplashScreen/SplashScreen.component';
import { Helmet } from 'react-helmet';
import { APP_NAME_POSTFIX } from '../../constants';
import {
  HAS_NO_RESULTS_DISCIPLINES,
  HAS_NO_TIMES_DISCIPLINES,
} from './constants';
import docIcon from '../../assets/icons/doc-empty.svg';
import arrowIcon from '../../assets/icons/arrow-right.svg';
import useChangeLocationSearch from '../../hooks/useChangeLocationSearch';
import { useScrollOnSearch } from '../../hooks/useScrollOnSearch';
import { useScrollToShowResults } from './hooks/useScrollToShowResults';

const connector = connect(
  (state: RootState) => ({
    searchQuery: state.app.searchQuery['summary'],
  }),
  {},
);

type PropsFromRedux = ConnectedProps<typeof connector>;
const Summary: FunctionComponent<PropsFromRedux> = ({ searchQuery }) => {
  const [query] = useQueryParams(AppQueryParamsConfigMap);

  const { data: activeEvent, isFetching } = useEventFeed({
    eventId: query.e,
    searchQuery,
  });

  const { c: classId, e: eventId } = query;
  const history = useHistory();

  const getModifiedSearchQuery = useChangeLocationSearch();

  const leftMenuCollapsed =
    (window.location.href.includes('/results') ||
      window.location.href.includes('/times')) &&
    classId &&
    eventId;

  const downloadText = window.location.href.includes('/results')
    ? 'Download Results'
    : 'Download Times';

  const downloadUrl = window.location.href.includes('/results')
    ? activeEvent?.resultsDocUrl
    : activeEvent?.timesDocUrl;

  const hasResults =
    activeEvent?.discipline &&
    !HAS_NO_RESULTS_DISCIPLINES.includes(activeEvent.discipline);

  const hasTimes =
    activeEvent?.discipline &&
    !HAS_NO_TIMES_DISCIPLINES.includes(activeEvent.discipline);

  const { width = 0 } = useWindowSize();

  const containerRef = useRef<HTMLDivElement>(null);
  const resultsBlockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, []);

  useScrollOnSearch(
    leftMenuCollapsed ? resultsBlockRef : containerRef,
    600,
    -120,
  );

  useScrollToShowResults(
    leftMenuCollapsed ? resultsBlockRef : containerRef,
    -120,
  );

  const renderTitle = () => (
    <Helmet>
      <title>
        {activeEvent?.eventName || 'Summary'} - {APP_NAME_POSTFIX}
      </title>
    </Helmet>
  );

  if (isFetching && !activeEvent) {
    return <SplashScreenComponent />;
  }
  if (!activeEvent) {
    return null;
  }

  if (
    activeEvent &&
    activeEvent.eventClassList.length === 0 &&
    searchQuery &&
    !isFetching
  ) {
    return (
      <EmptyPage title="Nothing found">
        {renderTitle()}
        Try entering different class number or class name
      </EmptyPage>
    );
  }

  if (
    activeEvent &&
    (activeEvent.eventClassList.length === 0 || activeEvent.active === false) &&
    !searchQuery &&
    !activeEvent.publishResultsLiveFeed &&
    !activeEvent.publishTimesLiveFeed
  ) {
    return (
      <EmptyPage title="Live Feed either not currently active">
        {renderTitle()}
        Or will show later when the Secretary approves it. <br />
        For more information on MyShowSecretary software <br /> please contact{' '}
        <a href="mailto:support@myshowsecretary.co.uk" className="link">
          support@myshowsecretary.co.uk
        </a>
      </EmptyPage>
    );
  }

  return (
    <StyledSummary leftMenuCollapsed={!!leftMenuCollapsed} ref={containerRef}>
      {renderTitle()}
      <div className="mr-10 classes-list">
        {classId && (
          <NavLink
            className="txt fs-14 fw-7 mb-10 text-center d-flex align-items-center mb-20 back-button"
            to={{
              pathname: '/feed',
              search: getModifiedSearchQuery([
                { param: 'e', value: query.e?.toString() },
                { param: 'c', value: undefined },
              ]),
            }}
          >
            <img
              src={arrowIcon}
              className="mr-10"
              style={{ transform: 'rotate(180deg)' }}
              alt=""
            />
            Back to Class List
          </NavLink>
        )}
        <div className="classes-list-inner">
          <div className="d-table w-100" style={{ borderSpacing: '0 10px' }}>
            {activeEvent.eventClassList.map((classItem) => (
              <SummaryItemComponent key={classItem.id} id={classItem.id} />
            ))}
          </div>
        </div>
        {leftMenuCollapsed && width > TABLET_BREAKPOINT && (
          <CollapseButtonLineComponent
            position="right"
            expanded={!leftMenuCollapsed}
            onClick={() => history.push(`/feed?e=${activeEvent.eventId}`)}
          />
        )}
      </div>
      {leftMenuCollapsed && (
        <div className="results-block" ref={resultsBlockRef}>
          <div className="d-flex align-items-center summary-nav">
            <ul className="nav nav-tabs" role="tablist">
              {hasTimes && (
                <li className="nav-item">
                  <NavLink
                    className="nav-link"
                    to={{
                      pathname: '/feed/times',
                      search: history.location.search,
                    }}
                  >
                    Times
                  </NavLink>
                </li>
              )}
              {hasResults && (
                <li className="nav-item">
                  <NavLink
                    className="nav-link"
                    to={{
                      pathname: '/feed/results',
                      search: history.location.search,
                    }}
                  >
                    Results
                  </NavLink>
                </li>
              )}
            </ul>

            {downloadUrl && (
              <a
                href={downloadUrl}
                download={downloadUrl.substring(
                  downloadUrl.lastIndexOf('/') + 1,
                )}
                className="txt fs-14 fw-7 txt-accent download-link d-flex"
              >
                <span className="mr-10">{downloadText}</span>{' '}
                <img src={docIcon} alt="" />
              </a>
            )}
          </div>
          <Switch>
            {hasTimes && (
              <Route path="/feed/times" component={TimesContainer} />
            )}
            {hasResults && (
              <Route path="/feed/results" component={ResultsContainer} />
            )}
            <Route
              path=""
              render={() => (
                <Redirect
                  to={`/feed${getModifiedSearchQuery([
                    { param: 'e', value: eventId?.toString() },
                    { param: 'c', value: undefined },
                  ])}`}
                />
              )}
            />
          </Switch>
        </div>
      )}
    </StyledSummary>
  );
};

export default connector(Summary);
